import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Link from "@/components/feature/LocaleLink";
import PageLayout from "@/components/feature/PageLayout";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/useLang";
import { CATEGORY_LABEL, SOLUTIONS, findSolution } from "@/data/solutions";

export default function SolutionDetailPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { slug } = useParams();
  const solution = findSolution(slug);

  if (!solution) return <NotFound />;

  const text = solution.text[lang];
  const gallery = (lang === "en" && solution.galleryEn) || solution.gallery;
  const related = solution.related
    ? SOLUTIONS.filter((s) => solution.related!.includes(s.slug))
    : SOLUTIONS.filter((s) => s.category === solution.category && s.slug !== solution.slug);

  return (
    <PageLayout
      eyebrow={CATEGORY_LABEL[solution.category][lang]}
      title={text.title}
      desc={text.tagline}
      seoTitle={text.seoTitle}
      seoDescription={text.seoDescription}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {solution.image && (
            <div className="rounded-lg overflow-hidden bg-background-100 border border-background-200 mb-8">
              <img src={(lang === "en" && solution.imageEn) || solution.image} alt={text.title} className="w-full max-h-[460px] object-cover" />
            </div>
          )}
          {solution.comingSoon && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-800 text-xs font-semibold mb-6">
              <i className="ri-time-line"></i>
              {text.statusDesc ?? t("solutions.comingSoonDesc")}
            </div>
          )}
          {text.introTitle && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{text.introTitle}</h2>
          )}
          <div className={`space-y-4 ${text.highlights ? "mb-8" : "mb-10"}`}>
            {text.intro.split("\n\n").map((para) => (
              <p key={para.slice(0, 24)} className="text-foreground-700 text-base md:text-lg leading-relaxed">{para}</p>
            ))}
          </div>
          {text.highlights && text.highlights.length > 0 && (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
              {text.highlights.map((h) => (
                <li key={h.title} className="flex items-start gap-3 p-4 rounded-lg bg-primary-50 border border-primary-200">
                  <i className="ri-checkbox-circle-fill text-xl text-primary-600 leading-none mt-0.5" aria-hidden="true"></i>
                  <div>
                    <div className="font-heading font-semibold text-foreground-950">{h.title}</div>
                    <p className="text-sm text-foreground-700 leading-relaxed mt-1">{h.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className={`grid grid-cols-1 ${text.features.length === 4 ? "md:grid-cols-2" : "md:grid-cols-3"} gap-4 mb-10`}>
            {text.features.map((f, i) => (
              <div key={f.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="font-heading text-2xl font-bold text-background-300 mb-3">0{i + 1}</div>
                <div className="font-heading font-semibold text-foreground-950 mb-2">{f.title}</div>
                <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {text.specs && text.specs.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">{text.specsTitle ?? (lang === "ko" ? "주요 사양" : "Specifications")}</h2>
              <dl className="rounded-lg border border-background-200 overflow-hidden text-sm">
                {text.specs.map((s, i) => (
                  <div key={s.label} className={`grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] ${i % 2 === 0 ? "bg-background-100" : "bg-background-50"}`}>
                    <dt className="px-4 py-3 font-semibold text-foreground-950 break-words">{s.label}</dt>
                    <dd className="px-4 py-3 text-foreground-700 break-words">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {text.useCases && text.useCases.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">{lang === "ko" ? "활용 분야" : "Where It's Used"}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {text.useCases.map((u) => (
                  <div key={u.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <div className="font-heading font-semibold text-foreground-950 mb-2">{u.title}</div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{u.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {text.support && text.support.length > 0 && (
            <section className="mb-10 p-6 rounded-lg bg-primary-50 border border-primary-200">
              <h2 className="font-heading text-xl font-bold text-primary-900 mb-4">{lang === "ko" ? "오션커넥트 공급 · 설치 지원" : "Ocean Connect Supply & Support"}</h2>
              <ul className="space-y-2.5">
                {text.support.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-primary-900 leading-relaxed">
                    <i className="ri-check-line text-lg leading-none mt-0.5" aria-hidden="true"></i>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {text.note && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-primary-50 border border-primary-200 text-primary-900 text-sm">
              <i className="ri-award-line text-lg mt-0.5"></i>
              <span>{text.note}</span>
            </div>
          )}

          {gallery && gallery.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">{t("solutions.screens")}</h2>
              <div className="grid grid-cols-1 gap-4">
                {gallery.map((src, i) => (
                  <figure key={src} className="rounded-lg overflow-hidden border border-background-200 bg-background-100">
                    <img src={src} alt={text.galleryCaptions?.[i] ?? ""} loading="lazy" decoding="async" className="w-full object-cover" />
                    {text.galleryCaptions?.[i] && (
                      <figcaption className="px-4 py-2.5 text-sm text-foreground-700">{text.galleryCaptions[i]}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-5">
          <div className="p-6 rounded-lg bg-foreground-950 text-background-50">
            <h3 className="font-heading font-bold text-lg mb-2">{t("solutions.cta.title")}</h3>
            <p className="text-sm text-foreground-300 leading-relaxed mb-5">{t("solutions.cta.desc")}</p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("solutions.cta.button")}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="tel:+82517150611"
              className="mt-3 flex items-center gap-2 text-sm text-background-100 hover:text-accent-300 cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              051-715-0611
            </a>
          </div>

          {related.length > 0 && (
            <div className="p-6 rounded-lg bg-background-100 border border-background-200">
              <h3 className="font-heading font-semibold text-foreground-950 mb-3">{t("solutions.related")}</h3>
              <ul className="space-y-1">
                {related.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/solutions/${s.slug}`}
                      className="flex items-center gap-2 py-2 text-sm text-foreground-700 hover:text-primary-600 cursor-pointer"
                    >
                      <i className={`${s.icon} text-primary-600`}></i>
                      {s.text[lang].title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            {t("solutions.back")}
          </Link>
        </aside>
      </div>
    </PageLayout>
  );
}
